import type { KeySummary } from "./api";

export interface TeamOption {
  label: string;
  value: string;
}

/** The filter value for keys that have no team. "" is any team; anything else is a team id. */
export const NO_TEAM = "__no_team";

type HasTeam = Pick<KeySummary, "teamId" | "teamAlias">;

/**
 * How a key's team is shown on Administration: its name, or its id's tail when the name cannot be
 * looked up (the service key may not list teams yet), or null for a key with no team.
 */
export function teamLabel(k: HasTeam): string | null {
  if (!k.teamId) return null;
  return k.teamAlias ?? `Team …${k.teamId.slice(-8)}`;
}

/**
 * The teams that appear on these keys, for the Team filter. Not every team that exists: most would
 * match nothing, and a key's own team is the only thing this filter looks at.
 */
export function teamOptions(keys: HasTeam[]): TeamOption[] {
  const seen = new Map<string, string>();
  for (const k of keys) if (k.teamId && !seen.has(k.teamId)) seen.set(k.teamId, teamLabel(k)!);
  return [...seen].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
}

export function matchesTeam(k: Pick<KeySummary, "teamId">, team: string): boolean {
  if (!team) return true;
  return team === NO_TEAM ? !k.teamId : k.teamId === team;
}
